/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  PlayerAudioTrack,
  usePlayerController,
} from "../../../../../BasePlayer";
import { useToggleVisibility } from "../../../../Base";

import { useAvailableSection } from "./AvailableSectionContext";

export const useAudioSettings = () => {
  const { player } = usePlayerController();
  const { toggleVisibility } = useToggleVisibility();
  const { setAvailableStatus } = useAvailableSection();

  const [tracks, setTracks] = useState<PlayerAudioTrack[]>([]);

  const activeTrack = useMemo(
    () => tracks.find((track) => track.active),
    [tracks],
  );

  const updateAudio = useCallback(() => {
    const audioTracks = player.audioTracks();

    if (audioTracks.length > 1) {
      setAvailableStatus("audio", true);
    }

    setTracks(player.audioTracks());
  }, [player, setAvailableStatus]);

  useEffect(() => {
    updateAudio();

    const unsubscribe = player.onAudioTrackChange(() => updateAudio());

    return () => unsubscribe();
  }, [player, setAvailableStatus, updateAudio]);

  const onTrackSelect = useCallback(
    (track: PlayerAudioTrack) => {
      player.setAudioTrack(track.id);
      toggleVisibility();
    },
    [player, toggleVisibility],
  );

  return {
    tracks,
    activeTrack,
    onTrackSelect,
  };
};
